import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import './style.scss';

import { GameRedirectionSource, NotificationType } from '../../../../enums';
import { integration } from '../../../../services/integration';
import ChooseRewardPopup from './choose-reward-popup';
import FreeSpinFooter from './free-spin-footer';

import { useStore, useTranslation } from '../../../../hooks';
import { setTranslationValues } from '../../../../helpers';
import FreeSpinHeader from './free-spin-header';
import { UserFreebet, UserFreebetGame } from '../../../../models';
import { env } from '../../../../environment';
import { T } from '../../../../constants';

interface FreeSpinProps {
  data: UserFreebet;
  allData: UserFreebet[];
  initialShowRewards?: boolean;
  onClose: (id: number, markAsSeen?: boolean) => void;
  onCloseAll: () => void;
}

const FreeSpin: React.FC<FreeSpinProps> = observer(({
  data,
  allData,
  initialShowRewards = false,
  onClose,
  onCloseAll,
}) => {
  const { freeSpinStore, gameStore, toastNotificationStore } = useStore();
  const { gameReady, hasGameRedirection } = gameStore;
  const isClaiming = freeSpinStore.claimFreeSpinsResponse.isLoading;

  const [selectedFreeBet, setSelectedFreeBet] = useState<UserFreebet | null>(null);
  const [activeFreeBet, setActiveFreeBet] = useState(data);
  const [selectedGame, setSelectedGame] = useState<UserFreebetGame | null>(null);
  const [showAllGames, setShowAllGames] = useState(false);
  const [showRewardList, setShowRewardList] = useState(initialShowRewards);
  const { freeBetGames = [] } = activeFreeBet || {};
  const translate = useTranslation();

  useEffect(() => {
    if (freeBetGames.length === 1) {
      setSelectedGame(freeBetGames[0]);
      return;
    }
  }, [freeBetGames]);

  const handleSelectFreeBet = (freeBet: UserFreebet) => {
    setSelectedFreeBet(freeBet);
  };

  const handleUseNow = async () => {
    if (showRewardList) {
      if (!selectedFreeBet) return;

      setActiveFreeBet(selectedFreeBet);
      setShowAllGames(false);
      setShowRewardList(false);
      return;
    }

    // read from the store, not the render closure: a double-click can land before the re-render
    if (!selectedGame || freeSpinStore.claimFreeSpinsResponse.isLoading) return;

    const response = await freeSpinStore.claimFreeSpins({
      freeBetId: activeFreeBet.id,
      gameIds: [selectedGame.gameId],
    });

    if (response?.errorMessage) return;

    // remove claimed free spin from store so it won't re-appear
    freeSpinStore.removeFreeSpin(activeFreeBet.id);

    // partners without game redirection cannot launch the chosen game for the player, so the claim
    // is the whole flow - the toaster tells them which game to open themselves
    if (!hasGameRedirection) {
      toastNotificationStore.enqueue(
        setTranslationValues(translate(T.FREE_SPINS_SUCCESSFULLY_CLAIMED), { gameName: selectedGame.name }),
        NotificationType.Success
      );
      onCloseAll();
      return;
    }

    if (integration.current?.changeGame) {
      integration.current.changeGame({
        gameId: selectedGame.gameId,
        source: GameRedirectionSource.Freebet,
      });
      return;
    }

  };

  const handleUseLater = () => {
    if (showRewardList) {
      onCloseAll();
      return;
    }
    const shouldMarkAsSeen = !activeFreeBet.seenByPlayer;
    onClose(activeFreeBet.id, shouldMarkAsSeen);
  };

  if (!gameReady) {
    return null;
  }

  return (
    <div className="FreeSpinWrapper">
      <div className={`FreeSpinHolder ${showRewardList ? 'recived-holder' : ''}`}>
        <div className="FreeSpin-body">
          {showRewardList ? (
            <ChooseRewardPopup
              allData={allData}
              selectedRewardId={selectedFreeBet?.id}
              onSelect={handleSelectFreeBet}
            />
          ) : (
            <>
              {!showAllGames && <FreeSpinHeader data={activeFreeBet} />}
              {
                <div className="amount-used-body-wrapper">
                  <div
                    className={`games-action-head-row ${!showAllGames ? 'state-right' : 'state-center'
                      }`}
                  >
                    <i className="icon-arrow-left popup-arrow-icon" onClick={() => setShowAllGames(false)} />
                    {!showAllGames && freeBetGames.length > 9 && (
                      <button
                        type="button"
                        className="FreeSpinButton state-black small"
                        onClick={() => setShowAllGames(true)}
                      >
                        <span className="FreeSpinButtonSmallText">{translate(T.SEE_ALL)}</span>
                      </button>
                    )}
                    <span className="games-action-head-row-title">{translate(T.CHOOSE_A_GAME)}</span>
                  </div>
                  <div className="games-action-container">
                    <div className={`games-action-cont-body ${freeBetGames.length === 1 ? 'single-game' : ''}`}>
                      {freeBetGames.map((game: UserFreebetGame) => {
                        const isSelected = selectedGame?.gameId === game?.gameId;
                        const thumbnail = game?.thumbnail ? `${env.filesUrl}/${game.thumbnail}` : '';

                        return (
                          <div
                            key={game?.gameId}
                            className={`games-action-box ${isSelected ? 'active' : ''}`}
                            onClick={() => setSelectedGame(game)}
                          >
                            {thumbnail && (
                              <img
                                className="games-box-image"
                                src={thumbnail}
                                alt={game?.name}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              }
            </>
          )}
        </div>

        <FreeSpinFooter
          primaryText={showRewardList ? T.USE_NOW : (hasGameRedirection ? T.PLAY_NOW : T.CLAIM_NOW)}
          primaryDisabled={showRewardList ? !selectedFreeBet : (!selectedGame || isClaiming)}
          onPrimaryClick={handleUseNow}
          onSecondaryClick={handleUseLater}
        />
      </div>
    </div>
  );
});

export default FreeSpin;
